/**
 * Feature data loader and role-based access utilities
 */

import React from 'react';
import type { User } from 'firebase/auth';
import {
  HomeIcon,
  Cog6ToothIcon,
  SparklesIcon,
  ChartBarIcon,
  WrenchScrewdriverIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import type { MenuGroup, FeatureItem } from '../types';
import { featureData } from './featuredata';

// Icon mapping function for JSON data
function getIcon(iconName: string, isGroupIcon: boolean = false): React.ReactNode {
  const className = isGroupIcon ? "h-5 w-5" : "h-4 w-4";
  
  switch (iconName) {
    case 'HomeIcon':
      return React.createElement(HomeIcon, { className });
    case 'Cog6ToothIcon':
      return React.createElement(Cog6ToothIcon, { className });
    case 'SparklesIcon':
      return React.createElement(SparklesIcon, { className });
    case 'ChartBarIcon':
      return React.createElement(ChartBarIcon, { className });
    case 'WrenchScrewdriverIcon':
      return React.createElement(WrenchScrewdriverIcon, { className });
    case 'ShieldCheckIcon':
      return React.createElement(ShieldCheckIcon, { className });
    default:
      return React.createElement(HomeIcon, { className });
  }
}

interface RawFeatureData {
  id: string;
  label: string;
  iconName: string;
  features: {
    id: string;
    label: string;
    iconName: string;
    tooltip?: string;
    toolboxRole: string;
  }[];
}

/**
 * Load feature data from JSON file and convert to MenuGroup format
 */
export async function loadFeatureData(): Promise<MenuGroup[]> {
  try {
    const rawData: RawFeatureData[] = featureData as unknown as RawFeatureData[];
    
    return rawData.map(group => ({
      id: group.id,
      label: group.label,
      icon: getIcon(group.iconName, true),
      features: group.features.map(feature => ({
        id: feature.id,
        label: feature.label,
        icon: getIcon(feature.iconName, false),
        iconName: feature.iconName,
        tooltip: feature.tooltip,
        toolboxRole: feature.toolboxRole as 'admin' | 'vss' | 'netzoom'
      }))
    }));
  } catch (error) {
    console.error('Error loading feature data:', error);
    // Return fallback data
    return [];
  }
}

/**
 * Get unique features for a user based on their role
 */
export function getUniqueFeatures(allFeatures: FeatureItem[]): FeatureItem[] {
  const uniqueFeatures = new Map<string, FeatureItem>();
  
  allFeatures.forEach(feature => {
    if (!uniqueFeatures.has(feature.id)) {
      uniqueFeatures.set(feature.id, feature);
    }
  });
  
  return Array.from(uniqueFeatures.values());
}

/**
 * Check if a feature is accessible to a user based on their role
 */
export function isFeatureAccessible(
  featureId: string, 
  userRole: 'admin' | 'vss' | 'netzoom' | null, 
  allFeatures: FeatureItem[]
): boolean {
  if (!userRole) return false;
  
  // Admin has access to all features
  if (userRole === 'admin') return true;
  
  // Check if there's a feature with matching ID and role
  return allFeatures.some(feature => 
    feature.id === featureId && feature.toolboxRole === userRole
  );
}

/**
 * Filter menu groups based on user role and return with accessibility info
 */
export function getAccessibleMenuGroups(
  menuGroups: MenuGroup[], 
  userRole: 'admin' | 'vss' | 'netzoom' | null
): MenuGroup[] {
  return menuGroups.map(group => {
    const uniqueFeatures = getUniqueFeatures(group.features);
    
    const featuresWithAccess = uniqueFeatures.map(feature => ({
      ...feature,
      disabled: !isFeatureAccessible(feature.id, userRole, group.features)
    }));
    
    return {
      ...group,
      features: featuresWithAccess
    };
  });
}

/**
 * Get user tenant ID and UID from Firebase Auth user object
 * This function is now async to properly fetch custom claims
 */
export async function getUserTenantAndUid(user: User | null): Promise<{ tenantId: string | null, uid: string | null }> {
  if (!user) {
    return { tenantId: null, uid: null };
  }
  
  // Extract UID directly from user object
  const uid = user.uid || null;
  
  // Extract tenant ID from custom claims using proper Firebase Auth method
  let tenantId: string | null = null;
  
  try {
    // Get custom claims from ID token result
    const idTokenResult = await user.getIdTokenResult();
    const customClaims = idTokenResult.claims;
    
    // Check for tenant ID in custom claims
    if (customClaims.tenantId) {
      tenantId = customClaims.tenantId as string;
    }
  } catch (error) {
    console.warn('Error getting custom claims from token:', error);
  }
  
  // Fallback: try to extract from email domain
  if (!tenantId) {
    const email = user.email || '';
    if (email.includes('@netzoom.com')) {
      tenantId = 'netzoom-uhjpe';
    }
  }
  
  // Default fallback to netzoom tenant (since all tenantIds have dash + unique ID format)
  if (!tenantId) {
    tenantId = 'netzoom-uhjpe';
  }
  
  return { tenantId, uid };
}

/**
 * Fetch user's custom claims using Cloud Run API
 * Migrated from Firebase Cloud Functions to Cloud Run
 */
export async function fetchUserCustomClaims(
  tenantId: string, 
  uid: string,
  user: User
): Promise<{ toolboxRole: 'admin' | 'vss' | 'netzoom' | null, customClaims: Record<string, unknown> }> {
  try {
    console.log('🔍 fetchUserCustomClaims called with:', { tenantId, uid });
    
    // Get Firebase ID token for authentication
    const idToken = await user.getIdToken();
    
    // Call Cloud Run API endpoint
    const cloudRunUrl = (window as any).APP_CONFIG?.CLOUDRUN_URL || 'https://n20-storage-cloudrun-mkvwooi2sa-uc.a.run.app';
    const response = await fetch(`${cloudRunUrl}/api/users/get`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        data: { tenantId, uid }
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Cloud Run API error:', errorText);
      return { toolboxRole: null, customClaims: {} };
    }
    
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.error('❌ Cloud Run API returned non-JSON response:', contentType);
      return { toolboxRole: null, customClaims: {} };
    }
    
    const result = await response.json();
    console.log('🔍 Cloud Run getUser result:', result);
    
    if (result.data && result.data.customClaims) {
      const customClaims = result.data.customClaims;
      const toolboxRole = result.data.toolboxRole || customClaims.toolboxRole || null;
      
      console.log('🔍 Custom claims retrieved:', { toolboxRole, customClaims });
      
      return {
        toolboxRole: (typeof toolboxRole === 'string' && ['admin', 'vss', 'netzoom'].includes(toolboxRole)) ? toolboxRole as 'admin' | 'vss' | 'netzoom' : null,
        customClaims
      };
    } else {
      console.warn('❌ No custom claims in response');
      return { toolboxRole: null, customClaims: {} };
    }
  } catch (error) {
    console.error('❌ Error fetching user custom claims:', error);
    return { toolboxRole: null, customClaims: {} };
  }
}
